import { Injectable } from '@angular/core';

/**
 * Servicio para acceder a carpeta física del sistema usando File System Access API
 * Solo funciona en Chrome/Edge (navegadores Chromium)
 */
@Injectable({
  providedIn: 'root',
})
export class FileSystemAccessService {
  private directoryHandle: FileSystemDirectoryHandle | null = null;
  private readonly STORAGE_KEY = 'procedimientos_directory_handle';
  private initialized = false;

  constructor() {
    // ✅ NUEVO: Auto-inicializar al crear el servicio
    this.initialize();
  }

  /**
   * ✅ NUEVO: Inicializa el servicio y restaura permisos guardados
   */
  private async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    console.log('🔧 Inicializando FileSystemAccessService...');

    if (!this.isSupported()) {
      console.warn('⚠️ File System Access API no soportado');
      this.initialized = true;
      return;
    }

    // Intentar cargar handle guardado
    await this.loadStoredDirectoryHandle();

    this.initialized = true;
    console.log('✅ FileSystemAccessService inicializado');
  }

  /**
   * Verifica si el navegador soporta File System Access API
   */
  isSupported(): boolean {
    return 'showDirectoryPicker' in window;
  }

  /**
   * Verifica si ya tenemos permiso a una carpeta
   */
  async hasDirectoryAccess(): Promise<boolean> {
    // Asegurar que está inicializado
    await this.initialize();

    if (!this.directoryHandle) {
      return false;
    }

    try {
      // Verificar que el permiso sigue válido
      const permission = await this.directoryHandle.queryPermission({ mode: 'readwrite' });

      if (permission === 'granted') {
        return true;
      }

      // ✅ NUEVO: Si el permiso está en 'prompt', intentar solicitarlo automáticamente
      if (permission === 'prompt') {
        console.log('🔐 Permiso en estado "prompt", solicitando...');
        const newPermission = await this.directoryHandle.requestPermission({ mode: 'readwrite' });
        return newPermission === 'granted';
      }

      return false;
    } catch (error) {
      console.warn('⚠️ Error verificando permiso:', error);
      return false;
    }
  }

  /**
   * Solicita permiso al usuario para seleccionar carpeta
   */
  async requestDirectoryAccess(): Promise<boolean> {
    if (!this.isSupported()) {
      console.warn('⚠️ File System Access API no soportado en este navegador');
      return false;
    }

    // ✅ NUEVO: Primero verificar si ya tenemos acceso
    const hasAccess = await this.hasDirectoryAccess();
    if (hasAccess) {
      console.log('✅ Ya se tiene acceso a la carpeta');
      return true;
    }

    try {
      // Mostrar diálogo para seleccionar carpeta
      this.directoryHandle = await window.showDirectoryPicker({
        mode: 'readwrite',
        startIn: 'documents',
      });

      // Guardar handle para persistencia
      await this.storeDirectoryHandle(this.directoryHandle);

      console.log('✅ Permiso de carpeta concedido:', this.directoryHandle.name);
      return true;
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('❌ Usuario canceló la selección de carpeta');
      } else {
        console.error('❌ Error solicitando permiso de carpeta:', error);
      }
      return false;
    }
  }

  /**
   * Guarda archivo en la carpeta seleccionada
   */
  async saveFile(fileName: string, blob: Blob, subfolder?: string): Promise<boolean> {
    // ✅ NUEVO: Verificar acceso antes de guardar
    const hasAccess = await this.hasDirectoryAccess();
    if (!hasAccess) {
      console.warn('⚠️ No hay acceso a carpeta. Solicita permiso primero.');
      return false;
    }

    if (!this.directoryHandle) {
      console.warn('⚠️ No hay carpeta seleccionada');
      return false;
    }

    try {
      let targetDirectory = this.directoryHandle;

      // Crear estructura de carpetas (soporta múltiples niveles)
      if (subfolder) {
        targetDirectory = await this.getOrCreateNestedSubfolder(this.directoryHandle, subfolder);
      }

      // Crear archivo
      const fileHandle = await targetDirectory.getFileHandle(fileName, { create: true });
      const writable = await fileHandle.createWritable();
      await writable.write(blob);
      await writable.close();

      console.log(`✅ Archivo guardado en carpeta física: ${subfolder ? subfolder + '/' : ''}${fileName}`);
      return true;
    } catch (error) {
      console.error('❌ Error guardando archivo en carpeta física:', error);
      return false;
    }
  }

  /**
   * Lee archivo de la carpeta
   */
  async readFile(fileName: string, subfolder?: string): Promise<Blob | null> {
    // ✅ NUEVO: Verificar acceso antes de leer
    const hasAccess = await this.hasDirectoryAccess();
    if (!hasAccess) {
      console.warn('⚠️ No hay acceso a carpeta');
      return null;
    }

    if (!this.directoryHandle) {
      console.warn('⚠️ No hay carpeta seleccionada');
      return null;
    }

    try {
      let targetDirectory = this.directoryHandle;

      if (subfolder) {
        targetDirectory = await targetDirectory.getDirectoryHandle(subfolder);
      }

      const fileHandle = await targetDirectory.getFileHandle(fileName);
      const file = await fileHandle.getFile();
      return file;
    } catch (error) {
      console.error('❌ Error leyendo archivo de carpeta física:', error);
      return null;
    }
  }

  /**
   * Obtiene información de la carpeta seleccionada
   */
  async getDirectoryInfo(): Promise<{ name: string; path?: string } | null> {
    if (!this.directoryHandle) {
      return null;
    }

    return {
      name: this.directoryHandle.name,
      // path no está disponible por seguridad
    };
  }

  /**
   * Limpia el permiso guardado (usuario quiere cambiar carpeta)
   */
  async clearStoredPermission(): Promise<void> {
    this.directoryHandle = null;

    try {
      const db = await this.openDatabase();
      if (db.objectStoreNames.contains('handles')) {
        const transaction = db.transaction(['handles'], 'readwrite');
        const store = transaction.objectStore('handles');
        await this.promisifyRequest(store.delete(this.STORAGE_KEY));
      }
      console.log('🗑️ Permiso de carpeta eliminado');
    } catch (error) {
      console.error('❌ Error eliminando permiso:', error);
    }
  }

  // ========== MÉTODOS PRIVADOS ==========

  /**
   * Guarda el handle de la carpeta en IndexedDB para persistencia
   */
  private async storeDirectoryHandle(handle: FileSystemDirectoryHandle): Promise<void> {
    try {
      const db = await this.openDatabase();
      const transaction = db.transaction(['handles'], 'readwrite');
      const store = transaction.objectStore('handles');
      await this.promisifyRequest(store.put(handle, this.STORAGE_KEY));
      console.log('💾 Permiso de carpeta guardado para persistencia');
    } catch (error) {
      console.error('❌ Error guardando permiso:', error);
      throw error;
    }
  }

  /**
   * ✅ MEJORADO: Carga el handle guardado de IndexedDB y verifica permisos
   */
  private async loadStoredDirectoryHandle(): Promise<void> {
    try {
      const db = await this.openDatabase();

      if (!db.objectStoreNames.contains('handles')) {
        console.log('ℹ️ No hay handles guardados (primera vez)');
        return;
      }

      const transaction = db.transaction(['handles'], 'readonly');
      const store = transaction.objectStore('handles');
      const handle = await this.promisifyRequest<FileSystemDirectoryHandle>(store.get(this.STORAGE_KEY));

      if (!handle) {
        console.log('ℹ️ No hay carpeta guardada');
        return;
      }

      console.log('📁 Carpeta guardada encontrada:', handle.name);

      // Verificar estado del permiso
      const permission = await handle.queryPermission({ mode: 'readwrite' });

      if (permission === 'granted') {
        this.directoryHandle = handle;
        console.log('✅ Permiso restaurado automáticamente');
      } else if (permission === 'prompt') {
        console.log('🔐 Permiso requiere confirmación del usuario');
        // Guardar el handle pero NO solicitar permiso aún
        // Se solicitará cuando se intente usar
        this.directoryHandle = handle;
      } else {
        console.log('❌ Permiso denegado para carpeta guardada');
        // Limpiar handle inválido
        await this.clearStoredPermission();
      }
    } catch (error) {
      console.warn('⚠️ No se pudo cargar permiso guardado:', error);
      // No es error crítico, simplemente no hay permisos guardados
    }
  }

  /**
   * Abre la base de datos de IndexedDB
   */
  private openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('FileSystemAccessDB', 1);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains('handles')) {
          db.createObjectStore('handles');
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Convierte una request de IndexedDB en una Promise
   */
  private promisifyRequest<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Obtiene o crea una subcarpeta
   */
  private async getOrCreateSubfolder(
    parentHandle: FileSystemDirectoryHandle,
    name: string,
  ): Promise<FileSystemDirectoryHandle> {
    return await parentHandle.getDirectoryHandle(name, { create: true });
  }

  /**
   * Obtiene o crea carpetas anidadas (ej: "REM-001/fotos")
   */
  private async getOrCreateNestedSubfolder(
    parentHandle: FileSystemDirectoryHandle,
    path: string,
  ): Promise<FileSystemDirectoryHandle> {
    // Dividir el path en partes (ej: "REM-001/fotos" → ["REM-001", "fotos"])
    const parts = path.split('/').filter((p) => p.length > 0);

    let currentHandle = parentHandle;

    // Crear cada carpeta en la ruta
    for (const part of parts) {
      currentHandle = await currentHandle.getDirectoryHandle(part, { create: true });
    }

    return currentHandle;
  }
}
